import { computed } from 'vue'
import type { Ref } from 'vue'
import useTranslatedSlug from './useTranslatedSlug'
import { useHead, useSeoMeta } from '#imports'
import { sanitizeCanonical } from '~/utils/seo'
import type { StoryblokSeoContent } from '~/cms/storyblok/types/storyblok.d.ts'
import { PAGE_META_BUS } from '#layers/base/constants/general'

interface MetaFallbacks {
  title?: string | undefined
  description?: string | undefined
}

interface MetaExtensions {
  link?: any[]
}
interface MetaOverrides {
  canonical?: string | undefined
  robots?: string | undefined
}

export default (args: {
  data: Ref<any>
  fetching?: Ref<boolean>
  extensions?: Ref<MetaExtensions>
  overrides?: Ref<MetaOverrides>
  fallbacks?: Ref<MetaFallbacks>
  ogType?: 'website' | 'article'
  ogImage?: Ref<string | undefined>
  articlePublishedTime?: Ref<string | undefined>
  articleModifiedTime?: Ref<string | undefined>
}) => {
  const config = useRuntimeConfig()
  const currentShop = useCurrentShop()
  const route = useRoute()
  const { data, fetching, extensions, fallbacks, overrides } = args

  const metaBus = useEventBus<string>(PAGE_META_BUS)

  const { name: translatedSlugName, path: translatedSlugPath } =
    useTranslatedSlug(data)

  const seo = computed(() => data.value?.content?.SEO as StoryblokSeoContent)
  const defaultRobots = computed(() =>
    data.value?.content?.noIndex ? 'noindex,follow' : 'index,follow',
  )
  const canonical = computed(() => {
    if (overrides?.value?.canonical === 'skip') {
      return
    }
    return (
      overrides?.value?.canonical ||
      (translatedSlugPath.value && translatedSlugPath.value !== 'home'
        ? sanitizeCanonical(
            `${config.public.baseUrl}/${currentShop.value.locale}/${translatedSlugPath.value}`,
          )
        : undefined)
    )
  })

  // og:url must be emitted unconditionally and must match the rendered
  // rel="canonical" so shared links resolve to the page's canonical URL.
  // The `canonical` computed above is intentionally skipped for PLPs
  // (overrides.canonical === 'skip'), but PLPs still render a canonical link
  // via extensions.link — and that one carries query params like ?page=.
  // Prefer, in order: the in-composable canonical, the canonical link emitted
  // through extensions, then the route.path-based absolute URL (home slug / no
  // Storyblok story). This keeps og:url and canonical from drifting.
  const extensionCanonical = computed(
    () =>
      extensions?.value?.link?.find((link) => link?.rel === 'canonical')?.href,
  )
  const ogUrl = computed(
    () =>
      canonical.value ||
      extensionCanonical.value ||
      sanitizeCanonical(`${config.public.baseUrl}${route.path}`),
  )

  // The Head object must be reactive to ensure that the `canonical` link is dynamically
  // added or removed based on the presence of a tag query in the URL.
  const head = computed(() => ({
    link: [
      ...(canonical.value
        ? [
            {
              rel: 'canonical',
              href: canonical.value,
            },
          ]
        : []),
      ...(extensions?.value?.link || []),
    ],
  }))

  useSeoMeta({
    title: () => seo.value?.title || fallbacks?.value?.title || '',
    description: () =>
      seo.value?.description || fallbacks?.value?.description || '',
    robots: () => overrides?.value?.robots || defaultRobots.value,
    // Open Graph baseline — emitted unconditionally so pages without a
    // Storyblok SEO block still receive type/url/title/description.
    ogType: () => args.ogType || 'website',
    ogUrl: () => ogUrl.value,
    ogTitle: () => seo.value?.title || fallbacks?.value?.title || '',
    ogDescription: () =>
      seo.value?.og_description ||
      seo.value?.description ||
      fallbacks?.value?.description ||
      '',
    // og:image: explicit per-page image (e.g. article header) first, then the
    // editor-set Storyblok og_image. No fabricated fallback (category stays
    // empty unless an editor set one).
    ogImage: () => args.ogImage?.value || seo.value?.og_image || undefined,
    // article:* tags only carry a value on the article path; omitted otherwise.
    articlePublishedTime: () => args.articlePublishedTime?.value || undefined,
    articleModifiedTime: () => args.articleModifiedTime?.value || undefined,
    ...(seo.value &&
      removeEmpty({
        twitterCard: seo.value.twitter_description,
        twitterTitle: seo.value.twitter_title,
        twitterImage: seo.value.twitter_image,
      })),
  })

  watch(
    () => fetching?.value,
    (value) => {
      metaBus.emit(value ? 'seo-meta:started' : 'seo-meta:finished')
    },
    { immediate: true },
  )

  metaBus.emit('seo-meta:finished')

  useHead(head)

  return {
    translatedSlugName,
    translatedSlugPath,
  }
}

function removeEmpty(obj: any) {
  const newObj: { [key: string]: any } = {}
  for (const [key, val] of Object.entries(obj)) {
    if (val) {
      newObj[key] = val
    }
  }
  return newObj
}
